import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Button } from "antd";
import { useTranslation } from "react-i18next";
import NotificationWithIcon from "./NotificationWithIcon";
import { arabicNameRules, cleanArabicInput } from "../utils/validators";
import { handleApiError } from "../utils/errorHandler";

const LocalizedNameFormModal = ({
  open,
  mode = "add",
  initialValues = null,
  onClose,
  onSuccess,
  createRequest,
  updateRequest,
  addTitleKey,
  editTitleKey,
  addSubmitKey,
  nameRequiredKey,
  addedTitleKey,
  addedDescriptionKey,
  updatedTitleKey,
  updatedDescriptionKey,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loadingBTN, setLoadingBTN] = useState(false);

  const isEdit = mode === "edit";
  const title = isEdit
    ? `${t(editTitleKey)}${initialValues?.name ? " : " + initialValues.name : ""}`
    : t(addTitleKey);
  const submitLabel = isEdit ? t("update") : t(addSubmitKey);

  useEffect(() => {
    if (!open) return;

    form.resetFields();

    if (isEdit && initialValues) {
      form.setFieldsValue({
        id: initialValues.id,
        name: initialValues.name,
        name_ar: initialValues.name_ar,
      });
    }
  }, [open, isEdit, initialValues, form]);

  const handleArabicNameChange = (e) => {
    form.setFieldsValue({
      name_ar: cleanArabicInput(e.target.value),
    });
  };

  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        setLoadingBTN(true);

        const request = isEdit ? updateRequest(values) : createRequest(values);

        request
          .then((response) => {
            const data = response?.data || response?.response?.data;

            if (data?.success === true) {
              NotificationWithIcon(
                "success",
                isEdit ? t(updatedTitleKey) : t(addedTitleKey),
                isEdit ? t(updatedDescriptionKey) : t(addedDescriptionKey),
              );

              form.resetFields();
              onSuccess?.(response, values);
              onClose?.();
              return;
            }

            if (data?.success === false) {
              handleApiError(response, t);
            }
          })
          .catch((err) => handleApiError(err, t))
          .finally(() => {
            setLoadingBTN(false);
          });
      })
      .catch(() => {});
  };

  const handleCancel = () => {
    form.resetFields();
    onClose?.();
  };

  return (
    <Modal
      open={open}
      title={title}
      onCancel={handleCancel}
      onOk={handleSubmit}
      footer={[
        <Button key="cancel" onClick={handleCancel} loading={loadingBTN}>
          {t("cancel")}
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loadingBTN}
          onClick={handleSubmit}
        >
          {submitLabel}
        </Button>,
      ]}
    >
      <Form layout="vertical" form={form} onFinish={handleSubmit}>
        <Form.Item
          name="name"
          label={t("name_en")}
          rules={[
            {
              required: true,
              message: t(nameRequiredKey),
              whitespace: true,
            },
          ]}
          hasFeedback
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="name_ar"
          label={t("name_ar")}
          rules={arabicNameRules(t, true)}
          hasFeedback
        >
          <Input
            dir="rtl"
            placeholder={t("arabic_only_validation")}
            onChange={handleArabicNameChange}
          />
        </Form.Item>

        <Form.Item name="id" hidden>
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default LocalizedNameFormModal;
